/**
 * @license
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2021 (original work) Open Assessment Technologies SA ;
 */

/**
 * @param {HTMLElement} node
 * @param {Object} param1
 * @param {Boolean} param1.subscribe - determines should we subscribe or not
 * @param {Number} [param1.delay=600] - delay before longpress event is fired
 * 600 is closest to native touch device delay
 * @returns {Object} directive config
 */
export default function longPress(node, { subscribe, delay = 600 }) {
    let isLongPress = false;
    let timeout;

    /**
     * Disables and enables context callout and text selection on long tap on mobile
     * @param {String|null} value - user-select value, can be null or 'none'
     */
    const setUserSelect = (value) => {
        document.body.style.userSelect = value;
        document.body.style['-webkit-touch-callout'] = value;
        document.body.style['-webkit-user-select'] = value;
        node.querySelectorAll('*').forEach(el => {
            el.style.userSelect = value;
            el.style['-webkit-touch-callout'] = value;
            el.style['-webkit-user-select'] = value;
        });
    };

    const dispatchLongPress = () => {
        const longPressEvt = new CustomEvent('longpress');
        node.dispatchEvent(longPressEvt);
    };

    const dispatchPress = () => {
        const pressEvt = new CustomEvent('press');
        node.dispatchEvent(pressEvt);
    };

    /**
     * Clear timeout on mouseup
     * Since 'click' event can be prevented on child components we have to do it in 'mouseup',
     * otherwise timer won't be stopped and next click will dispatch 'longpress' event
     */
    const removeTimeout = () => {
        clearTimeout(timeout);
        setUserSelect(null);
    };

    const handlePress = () => {
        setUserSelect('none');
        timeout = setTimeout(() => {
            isLongPress = true;
        }, delay);
    };

    /**
     * Clear timeout and reset isLongPress state on 'mouseout' so when we move mouse with
     * button down outside of element next click won't count as long press
     */
    const handleMouseOut = () => {
        removeTimeout();
        isLongPress = false;
    };

    /**
     * Dispatching 'longpress' or 'press' event must occur on 'click'
     * because mouseup event won't be stopped from propagation from child components
     * Also resets isLongPress, since 'click' occurs right after 'mouseup'
     */
    const handleClick = () => {
        if (isLongPress) {
            dispatchLongPress();
        } else {
            dispatchPress();
        }
        isLongPress = false;
    };

    const handleTouchEnd = () => {
        if (isLongPress) {
            dispatchLongPress();
        }
        /**
         * There's a quirk with Chrome on Android
         * on short tap 'click' event fires right after touchend
         * but on long tap it doesn't, so we have to handle long
         * press in touchend handler and short press will be handled
         * by click handler
         */
        isLongPress = false;
        removeTimeout();
    };

    const addEventListeners = () => {
        node.addEventListener('mousedown', handlePress);
        node.addEventListener('mouseup', removeTimeout);
        node.addEventListener('mouseout', handleMouseOut);
        node.addEventListener('click', handleClick);
        node.addEventListener('touchstart', handlePress);
        node.addEventListener('touchend', handleTouchEnd);
    };

    const clearEventListeners = () => {
        node.removeEventListener('mousedown', handlePress);
        node.removeEventListener('mouseup', removeTimeout);
        node.removeEventListener('mouseout', handleMouseOut);
        node.removeEventListener('click', handleClick);
        node.removeEventListener('touchstart', handlePress);
        node.removeEventListener('touchend', handleTouchEnd);
    };

    if (subscribe) {
        addEventListeners();
    }

    return {
        update({ subscribe: newSubscribe, delay: newDelay = 300 }) {
            if (newSubscribe) {
                delay = newDelay;
                clearEventListeners();
                addEventListeners();
            } else {
                clearEventListeners();
            }
        },
        destroy() {
            clearEventListeners();
        }
    };
}
